import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { queryKeys } from '@/query/keys';

import {
  mapFhirIdentifierMapping,
  mapFhirMappedFields,
  type FhirIdentifierMappingRequest,
  type FhirIdentifierMappingViewModel,
  type MappedFieldsViewModel,
  type UpdateFhirIdentifierMappingRequest,
} from './types';

/** Sort by explicit index, then FHIR system value, so the table order is stable. */
function sortMappings(
  rows: readonly FhirIdentifierMappingViewModel[],
): FhirIdentifierMappingViewModel[] {
  return [...rows].sort((a, b) => {
    const ai = a.indexNumber ?? Number.POSITIVE_INFINITY;
    const bi = b.indexNumber ?? Number.POSITIVE_INFINITY;
    if (ai !== bi) return ai - bi;
    return a.fhirSystemValue.localeCompare(b.fhirSystemValue);
  });
}

export function useFhirIdentifierMappings(
  integrationGuid: string,
): UseQueryResult<FhirIdentifierMappingViewModel[]> {
  return useQuery<FhirIdentifierMappingViewModel[]>({
    queryKey: queryKeys.fhirIdentifierMappings(integrationGuid),
    queryFn: async ({ signal }) => {
      const rows = await apiClient.getFhirIdentifierMappings(integrationGuid, { signal });
      return sortMappings(rows.map(mapFhirIdentifierMapping));
    },
  });
}

export function useFhirMappedFields(integrationGuid: string): UseQueryResult<MappedFieldsViewModel> {
  return useQuery<MappedFieldsViewModel>({
    queryKey: queryKeys.fhirMappedFields(integrationGuid),
    queryFn: async ({ signal }) =>
      mapFhirMappedFields(await apiClient.getFhirMappedFields(integrationGuid, { signal })),
  });
}

export function useFhirIdentifierMappingMutations(integrationGuid: string): {
  readonly create: UseMutationResult<
    FhirIdentifierMappingViewModel,
    Error,
    FhirIdentifierMappingRequest
  >;
  readonly update: UseMutationResult<
    FhirIdentifierMappingViewModel,
    Error,
    { guid: string; body: UpdateFhirIdentifierMappingRequest }
  >;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: queryKeys.fhirIdentifierMappings(integrationGuid) })
      .catch(() => undefined);
  };

  const create = useMutation<FhirIdentifierMappingViewModel, Error, FhirIdentifierMappingRequest>({
    mutationFn: async (body) =>
      mapFhirIdentifierMapping(await apiClient.createFhirIdentifierMapping(integrationGuid, body)),
    onSettled: invalidate,
  });

  const update = useMutation<
    FhirIdentifierMappingViewModel,
    Error,
    { guid: string; body: UpdateFhirIdentifierMappingRequest }
  >({
    mutationFn: async ({ guid, body }) =>
      mapFhirIdentifierMapping(
        await apiClient.updateFhirIdentifierMapping(integrationGuid, guid, body),
      ),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (guid) => apiClient.deleteFhirIdentifierMapping(integrationGuid, guid),
    onSettled: invalidate,
  });

  return { create, update, remove };
}

export function useUpdateFhirMappedFields(
  integrationGuid: string,
): UseMutationResult<MappedFieldsViewModel, Error, readonly string[]> {
  const queryClient = useQueryClient();
  return useMutation<MappedFieldsViewModel, Error, readonly string[]>({
    mutationFn: async (fields) =>
      mapFhirMappedFields(await apiClient.updateFhirMappedFields(integrationGuid, { fields })),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.fhirMappedFields(integrationGuid), next);
    },
    onSettled: () => {
      queryClient
        .invalidateQueries({ queryKey: queryKeys.fhirMappedFields(integrationGuid) })
        .catch(() => undefined);
    },
  });
}
